/**
 * The single TanStack Query client for the app.
 *
 * Every server read and write in this app goes through a GENERATED SDK hook —
 * there is no hand-written `fetch` anywhere in `src/`, and
 * no `useEffect` that fetches. This file only sets the defaults those hooks
 * inherit.
 *
 * Worth knowing when reading the options below: the generated SDK does not
 * reject on an API error. A failed call RESOLVES with `{ error, data: null }`,
 * so react-query counts it as a success and `onError` never fires for it. That
 * is why the error-modal wiring lives at the call site (`showApiError(result)`)
 * and the cache-level handler below is only about the case where the request
 * genuinely never produced an envelope.
 */
import {
  MutationCache,
  onlineManager,
  QueryCache,
  QueryClient,
  type QueryClientConfig,
} from "@tanstack/react-query";

import { seedOnlineStatus } from "./connectivity/seedOnlineStatus";
import { showOfflineError } from "./errors/apiErrorBus";

/** Long enough that tab-switching does not re-request, short enough that a
 * score or a standings row is never visibly stale. */
export const DEFAULT_STALE_TIME_MS = 30_000;

/** How long an unused cache entry survives — one navigation away and back
 * should paint instantly from cache before it revalidates. */
export const DEFAULT_GC_TIME_MS = 5 * 60_000;

export function buildQueryClientConfig(): QueryClientConfig {
  return {
    // A throw out of a generated hook means the response never became an
    // envelope at all: DNS failure, a captive portal returning HTML, an aborted
    // request. There is no error code to map, so the fan is told the connection
    // is the problem rather than being blamed for a server fault.
    queryCache: new QueryCache({
      onError: () => {
        if (onlineManager.isOnline()) showOfflineError();
      },
    }),
    mutationCache: new MutationCache({
      onError: () => {
        if (onlineManager.isOnline()) showOfflineError();
      },
    }),
    defaultOptions: {
      queries: {
        staleTime: DEFAULT_STALE_TIME_MS,
        gcTime: DEFAULT_GC_TIME_MS,
        // One retry, not three. A real failure surfaces to the fan in about a
        // second instead of after a silent 30-second backoff ladder that reads
        // as the app hanging.
        retry: 1,
        retryDelay: 500,
        // Coming back to the tab after a while is exactly when a fan wants to
        // see the current score, not what it was when they left.
        refetchOnWindowFocus: true,
        refetchOnReconnect: true,
        refetchOnMount: true,
      },
      mutations: {
        // Never automatic. A retried mutation is a second submitted pick.
        retry: 0,
      },
    },
  };
}

export function createAppQueryClient(): QueryClient {
  seedOnlineStatus();
  return new QueryClient(buildQueryClientConfig());
}
